import { Component, ElementRef, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { Campaign } from '../../models/campaign';
import { CampaignService } from '../../services/campaign.service';
import { CommonModule, formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Deliverable } from '../../models/deliverable';
import { CampaignTask } from '../../models/campaignTask';
import { Invoice, InvoiceLineItem } from '../../models/invoice';
import { FileType } from '../../models/filepayload';
import jsPDF from 'jspdf';
import { EditRecommendationsComponent } from "../edit-recommendations/edit-recommendations.component";
import { InvoiceService, toInvoiceAmount } from '../../services/invoice.service';
import { FileService } from '../../services/file.service';
import { Exclusivity } from '../../models/exclusivity';
import { Observable, Subject, debounceTime, distinctUntilChanged, switchMap } from 'rxjs';
import { GoogleDriveFile } from '../../models/googleDriveFile';
import { UploadResponse } from '../../models/uploadResponse';
import { UserService } from '../../services/user.service';
import { User, UserRole } from '../../models/user';
import { LoginService } from '../../services/login.service';
import { CompanyService } from '../../services/company.service';
import { Company } from '../../models/company';
import { RosterService } from '../../services/roster.service';
import { CampaignVideoService } from '../../services/campaign-video.service';
import { CampaignVideo } from '../../models/campaignVideo';
import { DialogComponent } from '../dialog/dialog.component';
import { CampaignChange } from '../../models/campaignChange';
import { CampaignChangeService } from '../../services/campaign-change.service';
import { brandLogoUrl, primaryDomain } from '../../utils/brand-logo';
import { rosterProfileIconUrl } from '../../utils/roster-image';

@Component({
  selector: 'app-campaign-main',
  imports: [CommonModule, FormsModule, RouterModule, EditRecommendationsComponent, DialogComponent],
  templateUrl: './campaign-main.component.html',
  styleUrl: './campaign-main.component.css'
})
export class CampaignMainComponent implements OnInit, OnDestroy {
  constructor(private route: ActivatedRoute, private campaignService: CampaignService, private invoiceService: InvoiceService, private fileService: FileService, private userService: UserService, private loginService: LoginService, private companyService: CompanyService, private rosterService: RosterService, private campaignVideoService: CampaignVideoService, private campaignChangeService: CampaignChangeService) {}

  get isAdmin(): boolean { return this.loginService.user?.role === UserRole.Admin; }

  getBrandName(brandId: string | undefined): string {
    if (!brandId) return '';
    return this.accounts.find(c => c.id === brandId)?.name ?? '';
  }

  // Header images. A URL that failed to load is remembered so the circle falls back to the
  // initial; tracking the URL (not a flag) lets a new brand/creator selection try again.
  private failedImageUrls = new Set<string>();

  get creatorImageUrl(): string {
    const url = this.campaign?.creatorId ? rosterProfileIconUrl(this.campaign.creatorId) : '';
    return url && !this.failedImageUrls.has(url) ? url : '';
  }

  get brandImageUrl(): string {
    const brand = this.accounts.find(c => c.id === this.campaign?.brandId);
    const domain = primaryDomain(brand?.domains);
    const url = domain ? brandLogoUrl(domain) : '';
    return url && !this.failedImageUrls.has(url) ? url : '';
  }

  onHeaderImageError(url: string): void {
    this.failedImageUrls.add(url);
  }

  // Subject for debouncing field changes
  private fieldUpdateSubject = new Subject<{fieldName: string, value: any}>();
  private pendingFieldUpdates = new Map<string, any>();
  private deliverableUpdateSubject = new Subject<{deliverable: any, fieldName: string, value: any}>();
  private exclusivityUpdateSubject = new Subject<{exclusivity: any, fieldName: string, value: any}>();
  private taskUpdateSubject = new Subject<{task: CampaignTask, fieldName: string, value: any}>();
  private fileUpdateSubject = new Subject<{campaignId: string, deliverableId: string, fileId: string, fieldName: string, value: any}>();
  private usageUpdateSubject = new Subject<{usageId: string, fieldName: string, value: any}>();

  @ViewChild('pdfPreview', { static: false }) pdfPreview!: ElementRef<HTMLDivElement>;
  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef<HTMLInputElement>;

  campaign!: Campaign;
  campaignObjectString: string = '';

  invoices: Invoice[] = [];
  isLoadingInvoices: boolean = false;

  files: GoogleDriveFile[] = [];
  isUploadingFile: boolean = false;
  isLoadingFiles: boolean = false;

  sortedTasks: CampaignTask[] = [];
  selectedTaskIds = new Set<string>();
  selectAllTasks: boolean = false;
  shiftDays: number = 1;

  // Phone layout: the Tasks grid is rendered as cards instead.
  static readonly SmallScreenQuery = '(max-width: 768px)';
  isSmallScreen: boolean = false;
  private smallScreenMediaQuery?: MediaQueryList;
  private onSmallScreenChange = (e: MediaQueryListEvent | MediaQueryList) => { this.isSmallScreen = e.matches; };

  selectedDeliverable: Deliverable | null = null;
  selectedInvoice: Invoice | null = null;
  selectedFile: GoogleDriveFile | null = null;

  // For adding files to deliverables
  selectedFileToAdd: string = '';
  fileDescriptionToAdd: string = '';

  agentUsers: User[] = [];
  castingUsers: User[] = [];
  accounts: Company[] = [];   // all companies — eligible as the account/payer role
  brands: Company[] = [];     // companies of type Brand — eligible as the advertiser role

  campaignVideos: CampaignVideo[] = [];
  isLoadingVideos: boolean = false;
  newVideoUrl: string = '';
  isAddingVideo: boolean = false;
  addVideoError: string = '';
  refreshingVideoId: string | null = null;
  copiedShareUrl: boolean = false;

  get publicAnalyticsUrl(): string {
    if (!this.campaign?.id) return '';
    return `${window.location.origin}/analytics/${this.campaign.id}`;
  }

  copyPublicAnalyticsUrl(): void {
    if (!this.publicAnalyticsUrl) return;
    navigator.clipboard.writeText(this.publicAnalyticsUrl).then(() => {
      this.copiedShareUrl = true;
      setTimeout(() => { this.copiedShareUrl = false; }, 1500);
    });
  }

  activeTab: string = 'properties';
  private invoicesLoaded: boolean = false;
  private filesLoaded: boolean = false;
  private videosLoaded: boolean = false;

  // Tasks, deliverables and exclusivities are not part of the campaign list; each is fetched from
  // its own endpoint the first time the user opens the matching tab, then kept for as long as this
  // campaign screen is open (like invoices / files below). Edits made on the screen are applied to
  // the same cached arrays by CampaignService, so switching tabs never needs to refetch. Leaving
  // the screen and coming back creates a new component and loads fresh data. A failed load is
  // retried the next time the tab is opened.
  private tasksLoaded: boolean = false;
  private deliverablesLoaded: boolean = false;
  private exclusivitiesLoaded: boolean = false;

  isLoadingTasks: boolean = false;
  isLoadingDeliverables: boolean = false;
  isLoadingExclusivities: boolean = false;

  setTab(tab: string): void {
    this.activeTab = tab;
    if (tab === 'tasks' && this.campaign && !this.tasksLoaded) {
      this.tasksLoaded = true;
      this.isLoadingTasks = true;
      this.campaignService.loadCampaignTasks(this.campaign.id).subscribe({
        next: (tasks) => {
          this.sortedTasks = tasks.sort((a, b) => { return a.dueDate < b.dueDate ? -1 : a.dueDate > b.dueDate ? 1 : 0; });
          this.isLoadingTasks = false;
        },
        error: (err) => { console.log(err); this.tasksLoaded = false; this.isLoadingTasks = false; }
      });
    }
    if (tab === 'deliverables' && this.campaign && !this.deliverablesLoaded) {
      this.deliverablesLoaded = true;
      this.isLoadingDeliverables = true;
      this.campaignService.loadCampaignDeliverables(this.campaign.id).subscribe({
        next: () => { this.isLoadingDeliverables = false; },
        error: (err) => { console.log(err); this.deliverablesLoaded = false; this.isLoadingDeliverables = false; }
      });
    }
    if (tab === 'exclusivities' && this.campaign && !this.exclusivitiesLoaded) {
      this.exclusivitiesLoaded = true;
      this.isLoadingExclusivities = true;
      this.campaignService.loadCampaignExclusivities(this.campaign.id).subscribe({
        next: () => { this.isLoadingExclusivities = false; },
        error: (err) => { console.log(err); this.exclusivitiesLoaded = false; this.isLoadingExclusivities = false; }
      });
    }
    if (tab === 'invoices' && !this.invoicesLoaded) {
      this.invoicesLoaded = true;
      this.isLoadingInvoices = true;
      this.invoiceService.getInvoicesByCampaign(this.campaign.id).subscribe({
        next: (result) => { this.invoices = result; this.isLoadingInvoices = false; },
        error: (err) => { console.log(err); this.isLoadingInvoices = false; }
      });
    }
    if (tab === 'files' && !this.filesLoaded) {
      this.filesLoaded = true;
      this.isLoadingFiles = true;
      this.fileService.GetGoogleDriveFiles(this.campaign.id).subscribe({
        next: (result) => { this.files = result; this.isLoadingFiles = false; },
        error: (err) => { console.log(err); this.isLoadingFiles = false; }
      });
    }
    if (tab === 'liveVideos' && !this.videosLoaded) {
      this.videosLoaded = true;
      this.loadCampaignVideos();
    }
    if (tab === 'changes' && this.campaign) {
      // Always refetch: the audit trail grows while the user edits other tabs.
      this.loadChanges();
    }
  }

  // ---- Changes tab ----
  changes: CampaignChange[] = [];
  isLoadingChanges: boolean = false;
  changesError: boolean = false;

  // Matches Constants.AiUserId in the API: edits made through n8n / AI have no signed-in user.
  private readonly aiUserId = '00000000-0000-0000-0000-00000000a1a1';

  loadChanges(): void {
    this.isLoadingChanges = true;
    this.changesError = false;
    this.viewedObjectCache.clear();
    this.campaignChangeService.getChanges(this.campaign.id).subscribe({
      next: (result) => { this.changes = result; this.isLoadingChanges = false; },
      error: (err) => { console.log(err); this.changesError = true; this.isLoadingChanges = false; }
    });
  }

  // Client-side filters for the Changes table. Empty string = no filter.
  changeFilterDate: string = '';
  changeFilterUser: string = '';
  changeFilterType: string = '';
  changeFilterField: string = '';
  changeFilterObjectId: string = '';

  get filteredChanges(): CampaignChange[] {
    const objectId = this.changeFilterObjectId.trim().toLowerCase();
    const date = this.changeFilterDate.trim();
    return this.changes.filter(c =>
      (!date || this.changeDateText(c).includes(date)) &&
      (!this.changeFilterUser || this.changeUserName(c) === this.changeFilterUser) &&
      (!this.changeFilterType || c.type === this.changeFilterType) &&
      (!this.changeFilterField || this.changeFieldLabel(c) === this.changeFilterField) &&
      (!objectId || c.objectId.toLowerCase().includes(objectId)));
  }

  // Same text the Date column displays (local time), so a filter matches what the user sees.
  changeDateText(c: CampaignChange): string {
    return formatDate(c.datetime, 'yyyy-MM-dd HH:mm:ss', 'en-US');
  }

  get changeUserOptions(): string[] {
    return [...new Set(this.changes.map(c => this.changeUserName(c)))].sort();
  }

  get changeTypeOptions(): string[] {
    return [...new Set(this.changes.map(c => c.type as string))].sort();
  }

  get changeFieldOptions(): string[] {
    return [...new Set(this.changes.map(c => this.changeFieldLabel(c)))].sort();
  }

  get hasChangeFilter(): boolean {
    return !!(this.changeFilterDate || this.changeFilterUser || this.changeFilterType || this.changeFilterField || this.changeFilterObjectId);
  }

  clearChangeFilters(): void {
    this.changeFilterDate = '';
    this.changeFilterUser = '';
    this.changeFilterType = '';
    this.changeFilterField = '';
    this.changeFilterObjectId = '';
  }

  // Read-only view of the object a change row refers to (every change type is clickable). Fetched
  // objects are cached by id so clicking the same object again makes no network call. The cache is
  // dropped whenever the change list is reloaded, so it never outlives the data it came from.
  showObjectDialog: boolean = false;
  viewedType: CampaignChange['type'] | null = null;
  viewedTask: CampaignTask | null = null;
  viewedDeliverable: Deliverable | null = null;
  viewedExclusivity: Exclusivity | null = null;
  viewedCampaign: Campaign | null = null;
  viewedInvoice: Invoice | null = null;
  isLoadingViewedObject: boolean = false;
  viewedObjectError: string = '';
  private viewedObjectCache = new Map<string, any>();

  isViewableChange(c: CampaignChange): boolean {
    return c.type === 'Task' || c.type === 'Deliverable' || c.type === 'Exclusivity' || c.type === 'Campaign' || c.type === 'Invoice';
  }

  viewChangedObject(c: CampaignChange): void {
    if (!this.isViewableChange(c)) return;

    this.viewedType = c.type;
    this.clearViewedObject();
    this.viewedObjectError = '';
    this.showObjectDialog = true;

    if (this.viewedObjectCache.has(c.objectId)) {
      this.setViewedObject(c.type, this.viewedObjectCache.get(c.objectId));
      return;
    }

    let request: Observable<any>;
    switch (c.type) {
      case 'Task': request = this.campaignService.getCampaignTask(this.campaign.id, c.objectId); break;
      case 'Deliverable': request = this.campaignService.getCampaignDeliverable(this.campaign.id, c.objectId); break;
      case 'Exclusivity': request = this.campaignService.getCampaignExclusivity(this.campaign.id, c.objectId); break;
      case 'Invoice': request = this.invoiceService.getCampaignInvoice(this.campaign.id, c.objectId); break;
      default: request = this.campaignService.getCampaignDetail(c.objectId); break;
    }

    this.isLoadingViewedObject = true;
    request.subscribe({
      next: (obj) => {
        this.viewedObjectCache.set(c.objectId, obj);
        this.isLoadingViewedObject = false;
        // The user may have closed the dialog or clicked another row while this was loading.
        if (this.showObjectDialog && this.viewedType === c.type) this.setViewedObject(c.type, obj);
      },
      error: (err) => {
        console.log(err);
        this.viewedObjectError = err?.status === 404
          ? `This ${c.type.toLowerCase()} no longer exists (it may have been deleted).`
          : `Could not load the ${c.type.toLowerCase()}.`;
        this.isLoadingViewedObject = false;
      }
    });
  }

  private clearViewedObject(): void {
    this.viewedTask = null;
    this.viewedDeliverable = null;
    this.viewedExclusivity = null;
    this.viewedCampaign = null;
    this.viewedInvoice = null;
  }

  private setViewedObject(type: CampaignChange['type'], obj: any): void {
    switch (type) {
      case 'Task': this.viewedTask = obj; break;
      case 'Deliverable': this.viewedDeliverable = obj; break;
      case 'Exclusivity': this.viewedExclusivity = obj; break;
      case 'Campaign': this.viewedCampaign = obj; break;
      case 'Invoice': this.viewedInvoice = obj; break;
    }
  }

  changeUserName(c: CampaignChange): string {
    return c.userId === this.aiUserId ? 'AI' : this.userService.resolveUserId(c.userId);
  }

  // Create / Delete rows carry a whole-document snapshot; show a short summary instead of the JSON.
  changeFieldLabel(c: CampaignChange): string {
    if (c.operation === 'Create') return 'Created';
    if (c.operation === 'Delete') return 'Deleted';
    return c.fieldName ?? '';
  }

  changeValueText(c: CampaignChange, which: 'old' | 'new'): string {
    if (c.operation !== 'Update') {
      const snapshot = which === 'old' ? c.oldValue : c.newValue;
      if (!snapshot) return '';
      const label = snapshot.name ?? snapshot.description ?? snapshot.invoiceNumber ?? snapshot.scope;
      return label ? String(label) : '';
    }
    const value = which === 'old' ? c.oldValue : c.newValue;
    if (value === null || value === undefined) return '';
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
  }

  get hasOverdueTasks(): boolean {
    if (!this.campaign?.tasks) return false;
    const today = new Date().toISOString().split('T')[0];
    return this.campaign.tasks.some(t => !t.complete && t.dueDate < today);
  }

  isTaskOverdue(t: CampaignTask): boolean {
    const today = new Date().toISOString().split('T')[0];
    return !t.complete && !!t.dueDate && t.dueDate < today;
  }

  resolveUserId(id: string): string { return this.userService.resolveUserId(id); }

  editPaidDate: boolean = false;

  showAddTimeline: boolean = false;

  FileType = FileType;
  fileType = Object.entries(FileType)
  .filter(([key, value]) => isNaN(Number(key)))
  .map(([key, value]) => ({
    label: key.replace(/([A-Z])/g, ' $1').trim(),
    value,
  }));

  ngOnInit(): void {
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.smallScreenMediaQuery = window.matchMedia(CampaignMainComponent.SmallScreenQuery);
      this.isSmallScreen = this.smallScreenMediaQuery.matches;
      this.smallScreenMediaQuery.addEventListener('change', this.onSmallScreenChange);
    }

    var campaignId = this.route.snapshot.params['campaignId'];

    this.userService.getUsers().subscribe(users => {
      this.agentUsers = users.filter(u => u.role === UserRole.Agent || u.role === UserRole.Scouting || u.role === UserRole.Casting || u.role === UserRole.Admin);
      // Source is restricted to Casting users (matches the Create New Campaign dialog)
      this.castingUsers = users.filter(u => u.role === UserRole.Casting);
    });

    this.companyService.getCompanies().subscribe(companies => {
      const sorted = companies.slice().sort((a, b) => a.name.localeCompare(b.name));
      this.accounts = sorted;                                  // any company can be the account/payer
      this.brands = sorted.filter(c => c.type === 'Brand');    // only Brands can be the advertiser
    });

    // The cached campaign comes from the lean list; fetch the full properties before showing it.
    this.campaignService.getCampaign(campaignId).pipe(
      switchMap(cached => this.campaignService.loadCampaignDetail(cached.id))
    ).subscribe({
      next: (result) => {
        this.campaign = result;
        this.sortedTasks = [];
        this.campaignObjectString = JSON.stringify(this.campaign, null, 2);

        if (!this.campaign.creatorName && this.campaign.creatorId) {
          this.rosterService.getRoster(this.campaign.creatorId).subscribe({
            next: (roster) => {
              this.campaign.creatorName = roster.name;
              this.campaign.tiktokHandle = roster.tiktokHandle;
            },
            error: (err) => { console.log(err); }
          });
        }
      },
      error: (err) => {
        console.log(err);
      }
    });

    // Setup debounced field updates
    this.fieldUpdateSubject.pipe(
      debounceTime(2000), // Wait 2 seconds after last change
      distinctUntilChanged((prev, curr) =>
        prev.fieldName === curr.fieldName && prev.value === curr.value
      )
    ).subscribe(({ fieldName, value }) => {
      this.pendingFieldUpdates.delete(fieldName);
      this.patchCampaignField(fieldName, value);
    });

    // Setup debounced deliverable updates
    this.deliverableUpdateSubject.pipe(
      debounceTime(2000), // Wait 2 seconds after last change
      distinctUntilChanged((prev, curr) =>
        prev.deliverable.id === curr.deliverable.id &&
        prev.fieldName === curr.fieldName &&
        prev.value === curr.value
      )
    ).subscribe(({ deliverable, fieldName, value }) => {
      this.patchDeliverable(deliverable, fieldName, value);
    });

    // Setup debounced exclusivity updates
    this.exclusivityUpdateSubject.pipe(
      debounceTime(2000), // Wait 2 seconds after last change
      distinctUntilChanged((prev, curr) =>
        prev.exclusivity.id === curr.exclusivity.id &&
        prev.fieldName === curr.fieldName &&
        prev.value === curr.value
      )
    ).subscribe(({ exclusivity, fieldName, value }) => {
      this.patchExclusivity(exclusivity, fieldName, value);
    });

    // Setup debounced task updates
    this.taskUpdateSubject.pipe(
      debounceTime(1000), // Wait 1 second after last change
      distinctUntilChanged((prev, curr) =>
        prev.task.id === curr.task.id &&
        prev.fieldName === curr.fieldName &&
        prev.value === curr.value
      )
    ).subscribe(({ task, fieldName, value }) => {
      this.patchTask(task, fieldName, value);
    });

    // Setup debounced file updates
    this.fileUpdateSubject.pipe(
      debounceTime(500), // Wait 500ms after last change
      distinctUntilChanged((prev, curr) =>
        prev.campaignId === curr.campaignId &&
        prev.deliverableId === curr.deliverableId &&
        prev.fileId === curr.fileId &&
        prev.fieldName === curr.fieldName &&
        prev.value === curr.value
      )
    ).subscribe(({ campaignId, deliverableId, fileId, fieldName, value }) => {
      this.patchDeliverableFile(campaignId, deliverableId, fileId, fieldName, value);
    });

    // Setup debounced usage updates
    this.usageUpdateSubject.pipe(
      debounceTime(1000), // Wait 1 second after last change
      distinctUntilChanged((prev, curr) =>
        prev.usageId === curr.usageId &&
        prev.fieldName === curr.fieldName &&
        prev.value === curr.value
      )
    ).subscribe(({ usageId, fieldName, value }) => {
      this.onUsageChange(usageId, fieldName, value);
    });
  }

  ngOnDestroy(): void {
    this.smallScreenMediaQuery?.removeEventListener('change', this.onSmallScreenChange);
    this.pendingFieldUpdates.forEach((value, fieldName) => {
      this.patchCampaignField(fieldName, value);
    });
    this.fieldUpdateSubject.complete();
    this.deliverableUpdateSubject.complete();
    this.exclusivityUpdateSubject.complete();
    this.taskUpdateSubject.complete();
    this.fileUpdateSubject.complete();
    this.usageUpdateSubject.complete();
  }

  getTotalInvoiceAmount(i: Invoice): number {
    return i.lineItems.reduce((total, item) => total + (item.unitPrice || 0), 0);
  }

  addDeliverable() {
    const newDeliverable = { "id": crypto.randomUUID(), "description": "New Deliverable", "caption": "", "postInstructions": "", "adDisclosure": "", "files": [], "liveLink": "" };

    this.campaignService.AddCampaignDeliverable(this.campaign.id, newDeliverable).subscribe({
      next: () => {
        console.log('Deliverable created successfully');
      },
      error: (error) => {
        console.error('Failed to create deliverable:', error);
      }
    });
  }

  addFileToDeliverable() {
    if (!this.selectedDeliverable || !this.selectedFileToAdd || !this.fileDescriptionToAdd?.trim()) {
      return;
    }

    // Initialize files array if it doesn't exist
    if (!this.selectedDeliverable.files) {
      this.selectedDeliverable.files = [];
    }

    // Check if file is already added
    const existingFile = this.selectedDeliverable.files.find(f => f.fileId === this.selectedFileToAdd);
    if (existingFile) {
      console.warn('This file is already attached to this deliverable.');
      return;
    }

    // Store values to clear form after successful API call
    const fileId = this.selectedFileToAdd;
    const description = this.fileDescriptionToAdd.trim();

    // Call the new API endpoint to persist the file to deliverable
    this.campaignService.addFileToDeliverable(
      this.campaign.id,
      this.selectedDeliverable.id,
      fileId,
      description
    ).subscribe({
      next: () => {
        console.log('File added to deliverable successfully');

        // Add to local array only after successful API call
        const newDFile = {
          fileId: fileId,
          description: description,
          brandFeedback: '',
          approved: false
        };
        this.selectedDeliverable!.files.push(newDFile);

        // Clear the form
        this.selectedFileToAdd = '';
        this.fileDescriptionToAdd = '';
      },
      error: (error) => {
        console.error('Failed to add file to deliverable:', error);
      }
    });
  }

  removeFileFromDeliverable(index: number) {
    if (!this.selectedDeliverable || !this.selectedDeliverable.files) {
      return;
    }

    // Remove file from array
    this.selectedDeliverable.files.splice(index, 1);

    // Update the deliverable via API
    this.onDeliverableChange(this.selectedDeliverable.id, 'files', this.selectedDeliverable.files);
  }

  addUsage() {
    if (!this.selectedDeliverable) {
      return;
    }

    // Add default usage with some sensible defaults
    const defaultType = 'Organic Usage';
    const defaultDuration = 30;

    // Call the API endpoint to persist the usage to deliverable
    this.campaignService.addUsageToDeliverable(
      this.campaign.id,
      this.selectedDeliverable.id,
      defaultType,
      defaultDuration
    ).subscribe({
      next: (usageId) => {
        console.log('Usage added to deliverable successfully');

        // Initialize usage array if it doesn't exist
        if (!this.selectedDeliverable!.usage) {
          this.selectedDeliverable!.usage = [];
        }

        // Add to local array with the same ID that was sent to the server
        const newUsage = {
          id: usageId, // Use the ID returned from the service
          type: defaultType,
          durationInDays: defaultDuration
        };
        this.selectedDeliverable!.usage.push(newUsage);
      },
      error: (error) => {
        console.error('Failed to add usage to deliverable:', error);
      }
    });
  }

  onUsageChangeDebounced(usageId: string, fieldName: string, value: any) {
    if (!this.selectedDeliverable) return;

    // Validation: don't allow null/undefined/empty string for durationInDays
    if (fieldName === 'durationInDays' && (value === null || value === undefined || value === '')) {
      // Force the field back to 0 to prevent null state
      const usage = this.selectedDeliverable.usage?.find(u => u.id === usageId);
      if (usage) {
        // Use setTimeout to ensure the UI updates after the current digest cycle
        setTimeout(() => {
          usage.durationInDays = 0;
        }, 0);
      }
      return;
    }

    // Update the local value immediately for UI responsiveness
    const usage = this.selectedDeliverable.usage?.find(u => u.id === usageId);
    if (usage) {
      (usage as any)[fieldName] = value;
    }

    // Queue the debounced update to the server (allow 0 as a valid value)
    this.usageUpdateSubject.next({
      usageId,
      fieldName,
      value
    });
  }

  removeUsageFromDeliverable(index: number) {
    if (!this.selectedDeliverable || !this.selectedDeliverable.usage) {
      return;
    }

    const usageToDelete = this.selectedDeliverable.usage[index];
    if (!usageToDelete) {
      return;
    }

    const deliverableId = this.selectedDeliverable.id;

    // Call the new API endpoint to delete the usage
    this.campaignService.deleteUsageFromDeliverable(
      this.campaign.id,
      this.selectedDeliverable.id,
      usageToDelete.id
    ).subscribe({
      next: () => {
        console.log('Usage deleted successfully');
        // Re-sync selectedDeliverable with the updated campaign object from the cache
        const updatedDeliverable = this.campaign.deliverables.find(d => d.id === deliverableId);
        if (updatedDeliverable) {
          this.selectedDeliverable = updatedDeliverable;
        }
      },
      error: (error) => {
        console.error('Failed to delete usage:', error);
      }
    });
  }

  addExclusivity() {
    const newExclusivity = { id: crypto.randomUUID(), startDate: new Date().toISOString().slice(0,10), endDate: new Date().toISOString().slice(0,10), scope: 'New Exclusivity' };

    this.campaignService.AddCampaignExclusivity(this.campaign.id, newExclusivity).subscribe({
      next: () => {
        console.log('Exclusivity created successfully');
      },
      error: (error) => {
        console.error('Failed to create exclusivity:', error);
      }
    });
  }

  addTask() {
    const newTask = new CampaignTask();

    this.campaignService.AddCampaignTask(this.campaign.id, newTask).subscribe({
      next: () => {
        console.log('Task created successfully');
      },
      error: (error) => {
        console.error('Failed to create task:', error);
      }
    });
  }

  deleteTask(taskToDelete: CampaignTask) {
    this.campaignService.DeleteTask(this.campaign.id, taskToDelete.id).subscribe({
      next: () => {
        console.log('Task deleted successfully');
        // Re-sort tasks after deletion
        this.sortedTasks = this.campaign.tasks.sort((a, b) => {
          return a.dueDate < b.dueDate ? -1 : a.dueDate > b.dueDate ? 1 : 0;
        });
      },
      error: (error) => {
        console.error('Failed to delete task:', error);
      }
    });
  }

  deleteExclusivity(exclusivityToDelete: Exclusivity) {
    this.campaignService.DeleteExclusivity(this.campaign.id, exclusivityToDelete.id).subscribe({
      next: () => {
        console.log('Exclusivity deleted successfully');
      },
      error: (error) => {
        console.error('Failed to delete exclusivity:', error);
      }
    });
  }

  moveDeliverable(index: number, direction: number) {
    const deliverables = this.campaign.deliverables;
    if (!deliverables) return;
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= deliverables.length) return;

    [deliverables[index], deliverables[newIndex]] = [deliverables[newIndex], deliverables[index]];

    const orderedIds = deliverables.map(d => d.id);
    this.campaignService.reorderDeliverables(this.campaign.id, orderedIds).subscribe({
      error: (err) => {
        console.error('Failed to reorder deliverables:', err);
        [deliverables[index], deliverables[newIndex]] = [deliverables[newIndex], deliverables[index]];
      }
    });
  }

  deleteDeliverable(dToDelete: Deliverable) {
    // Clear selection if this deliverable is currently selected
    if (this.selectedDeliverable && this.selectedDeliverable.id === dToDelete.id) {
      this.selectedDeliverable = null;
    }

    this.campaignService.DeleteDeliverable(this.campaign.id, dToDelete.id).subscribe({
      next: () => {
        console.log('Deliverable deleted successfully');
      },
      error: (error) => {
        console.error('Failed to delete deliverable:', error);
      }
    });
  }

  addInvoice() {
    const today = new Date();
    const dueDate = new Date();
    dueDate.setDate(today.getDate() + 30);

    let newInvoice: Invoice = {
      id: crypto.randomUUID(),
      campaignId: this.campaign.id,
      invoiceNumber: '',
      paidDate: '',
      creatorPaidDate: '',
      inRef: '',
      outRef: '',
      issueDate: today.toISOString().slice(0, 10),
      dueDate: dueDate.toISOString().slice(0, 10),
      billTo: '',
      invoiceNotes: '',
      lineItems: [],
      statusNote: '',
      creatorFacingStatus: ''
    };

    if (this.campaign) {
      // Set the invoice number to the first available invoice number, which might be '0001' if 0002 is the only invoice.

      for (let i = 1; i <= 50; i++) {
        // Check to see if invoice i is already taken.
        const invoiceNumber = this.campaign.id.substring(0, 8).toUpperCase() + '-' + i.toString().padStart(4, '0');

        if (!this.invoices.some(invoice => invoice.invoiceNumber === invoiceNumber)) {
          newInvoice.invoiceNumber = invoiceNumber;
          break;
        }
      }

      // If an invoice already exists, then set the same billTo* fields.
      if (this.invoices.length > 0) {
        newInvoice.billTo = this.invoices[0].billTo;
      }
    }

    this.invoices.push(newInvoice);

    this.invoiceService.saveNewInvoice(newInvoice).subscribe({
      next: (saved) => {
        console.log('Invoice created successfully!', saved);

        // Adopt the stored copy so this list and the service cache hold the same object.
        // Field patches mutate the instance in place, so they'd otherwise leave the cached
        // copy of a just-created invoice stale.
        const idx = this.invoices.findIndex(i => i.id === newInvoice.id);
        if (idx >= 0) {
          this.invoices[idx] = saved;
        }
        if (this.selectedInvoice === newInvoice) {
          this.selectedInvoice = saved;
        }
      },
      error: (err) => {
        console.error('Failed to create invoice:', err);
      }
    });

  }

  selectDeliverable(d: Deliverable) {
    this.selectedInvoice = null;
    this.selectedFile = null;
    this.selectedDeliverable = d;
  }

  selectFile(f: GoogleDriveFile) {
    this.selectedInvoice = null;
    this.selectedDeliverable = null;
    this.selectedFile = f;
  }

  addLineItem() {
    if (!this.selectedInvoice) return;

    const newLineItem: InvoiceLineItem = {
      description: 'New Line Item',
      unitPrice: 0
    }

    // The server appends and returns the stored invoice. Line items are addressed by their
    // array position, so take the persisted array rather than pushing locally - that keeps
    // the indexes the subsequent patches use aligned with what is actually stored.
    this.invoiceService.addLineItem(this.selectedInvoice.id, newLineItem).subscribe({
      next: (saved) => {
        this.selectedInvoice!.lineItems = saved.lineItems ?? [];
        this.refreshInvoicePdf();
      },
      error: (err) => {
        console.error('Failed to add line item:', err);
      }
    });
  }

  deleteInvoice(invoiceToDelete: Invoice) {
    this.invoices = this.invoices.filter(i => i.invoiceNumber !== invoiceToDelete.invoiceNumber);
    this.selectedInvoice = null;
    this.invoiceService.deleteInvoice(invoiceToDelete.id).subscribe({
      next: (response) => {
        console.log('Invoice deleted successfully!', response);
      },
      error: (err) => {
        console.error('Failed to delete invoice:', err);
      }
    });
  }

  deleteGoogleDriveFile(fileToDelete: GoogleDriveFile) {
    // Clear selection if this file is currently selected
    if (this.selectedFile && this.selectedFile.id === fileToDelete.id) {
      this.selectedFile = null;
    }

    this.fileService.deleteGoogleDriveFile(fileToDelete.id).subscribe({
      next: (response) => {
        console.log('File deleted successfully!', response);
        // Remove the file from the local array
        this.files = this.files.filter(f => f.id !== fileToDelete.id);
      },
      error: (err) => {
        console.error('Failed to delete file:', err);
      }
    });
  }

  addFile() {
    // Trigger the hidden file input
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.uploadFile(file);
    }
  }

  private uploadFile(file: File) {
    if (!this.campaign?.id) {
      console.error('No campaign ID available for file upload');
      return;
    }

    console.log('Starting file upload process for:', file.name);

    // Step 1: Clear files array and show loading state
    this.files = [];
    this.selectedFile = null;
    this.isUploadingFile = true;

    // Step 2: Get upload URL
    this.fileService.getUploadUrl(this.campaign.id, file.name, file.type, file.size).subscribe({
      next: (uploadResponse: UploadResponse) => {
        console.log('Upload URL received:', uploadResponse);

        // Step 3: Upload file to Google Drive
        this.fileService.uploadFileToGoogleDrive(uploadResponse.uploadUrl, file, uploadResponse.chunkSize).subscribe({
          next: (uploadResult) => {
            console.log('File uploaded successfully to Google Drive:', uploadResult);

            // Step 4: Refresh the files list to show the new file
            this.refreshFilesList();
          },
          error: (err) => {
            console.error('Failed to upload file to Google Drive:', err);
            this.isUploadingFile = false;
            // Reload files list even on error to restore the previous state
            this.refreshFilesList();
          }
        });
      },
      error: (err) => {
        console.error('Failed to get upload URL:', err);
        this.isUploadingFile = false;
        // Reload files list on error to restore the previous state
        this.refreshFilesList();
      }
    });
  }

  isUploadingFea: boolean = false;
  feaError: string = '';

  onFeaSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !this.campaign?.id) return;

    this.isUploadingFea = true;
    this.feaError = '';
    this.fileService.UploadFea(this.campaign.id, file).subscribe({
      next: (result) => {
        this.campaign.fea = result.fea;
        this.isUploadingFea = false;
        if (!result.processingStarted) {
          console.warn('FEA saved but processing workflow was not started:', result.processingError);
        }
      },
      error: (err) => {
        console.error('Failed to upload FEA:', err);
        this.feaError = typeof err?.error === 'string' && err.error ? err.error : 'Upload failed';
        this.isUploadingFea = false;
      }
    });
  }

  isDeletingFea: boolean = false;

  deleteFea() {
    if (!this.campaign?.id || !this.campaign.fea) return;

    this.isDeletingFea = true;
    this.feaError = '';
    this.fileService.DeleteFea(this.campaign.id).subscribe({
      next: () => {
        this.campaign.fea = null;
        this.isDeletingFea = false;
      },
      error: (err) => {
        console.error('Failed to delete FEA:', err);
        this.feaError = typeof err?.error === 'string' && err.error ? err.error : 'Delete failed';
        this.isDeletingFea = false;
      }
    });
  }

  private refreshFilesList() {
    if (this.campaign?.id) {
      this.isLoadingFiles = true;
      this.fileService.GetGoogleDriveFiles(this.campaign.id).subscribe({
        next: (result) => {
          this.files = result;
          this.isUploadingFile = false;
          this.isLoadingFiles = false;
          console.log('Files list refreshed');
        },
        error: (err) => {
          console.error('Failed to refresh files list:', err);
          this.isUploadingFile = false;
          this.isLoadingFiles = false;
        }
      });
    }
  }

  deleteLineItem(index: number) {
    if (!this.selectedInvoice) return;

    // Removal is by index and shifts everything after it, so only drop the local entry once
    // the server confirms - otherwise a failed delete would leave the indexes misaligned.
    this.invoiceService.removeLineItem(this.selectedInvoice.id, index).subscribe({
      next: () => {
        this.selectedInvoice!.lineItems.splice(index, 1);
        this.refreshInvoicePdf();
      },
      error: (err) => {
        console.error('Failed to delete line item:', err);
      }
    });
  }

  // Invoice edits patch one field at a time, with no debouncing: the pickers (dates, line
  // item type) patch immediately on (ngModelChange) since a calendar/dropdown selection is
  // one deliberate change, and the free-text inputs patch on (blur), which is already one
  // event per edit. Contrast the campaign/task/deliverable fields, which patch on every
  // keystroke and so need the debounce subjects.
  // refreshPdf: only the "Invoice Fields" inputs (dates, bill-to, notes, line items)
  // affect the rendered PDF. Status/payment fields save without re-rendering the preview.
  onInvoiceFieldChange(fieldName: string, value: any, refreshPdf: boolean = false) {
    if (!this.selectedInvoice) return;

    // The pay amounts are bound to text inputs, so ngModel writes a *string* back onto the
    // invoice - and that object is the same instance InvoiceService caches and hands to every
    // other page. Coerce here so pages that sum these fields add instead of concatenating.
    if (fieldName === 'actualPayAmount' || fieldName === 'creatorPayAmount') {
      value = toInvoiceAmount(value) ?? null;
      (this.selectedInvoice as any)[fieldName] = value ?? undefined;
    }

    this.invoiceService.patchInvoiceField(this.selectedInvoice.id, fieldName, value).subscribe({
      next: () => {
        console.log(`Invoice ${fieldName} updated successfully!`);
        if (refreshPdf) {
          this.refreshInvoicePdf();
        }
      },
      error: (err) => {
        console.error(`Failed to update invoice ${fieldName}:`, err);
      }
    });
  }

  onLineItemChange(index: number, fieldName: string, value: any) {
    if (!this.selectedInvoice) return;

    this.invoiceService.patchLineItemField(this.selectedInvoice.id, index, fieldName, value).subscribe({
      next: () => {
        console.log(`Invoice line item ${index} ${fieldName} updated successfully!`);
        this.refreshInvoicePdf();
      },
      error: (err) => {
        console.error(`Failed to update line item ${index} ${fieldName}:`, err);
      }
    });
  }

  onFieldChange(fieldName: string, value: any) {
    // Boolean fields, source, and dropdown fields get patched immediately, others are debounced
    const immediateFields = ['source', 'agentId', 'coordinatorId', 'accountId', 'brandId'];
    if (typeof value === 'boolean' || immediateFields.includes(fieldName)) {
      this.patchCampaignField(fieldName, value);
    } else {
      this.pendingFieldUpdates.set(fieldName, value);
      this.fieldUpdateSubject.next({ fieldName, value });
    }
  }

  onDeliverableChange(deliverableId: string, fieldName: string, value: any) {
    const deliverable = this.campaign.deliverables.find(d => d.id === deliverableId);
    if (!deliverable) return;

    // Boolean fields get patched immediately, others are debounced
    if (typeof value === 'boolean') {
      this.patchDeliverable(deliverable, fieldName, value);
    } else {
      // Queue the deliverable update for debounced processing
      this.deliverableUpdateSubject.next({ deliverable, fieldName, value });
    }
  }

  onExclusivityChange(exclusivityId: string, fieldName: string, value: any) {
    const exclusivity = this.campaign.exclusivities.find(e => e.id === exclusivityId);
    if (!exclusivity) return;

    // Boolean fields get patched immediately, others are debounced
    if (typeof value === 'boolean') {
      this.patchExclusivity(exclusivity, fieldName, value);
    } else {
      // Queue the exclusivity update for debounced processing
      this.exclusivityUpdateSubject.next({ exclusivity, fieldName, value });
    }
  }

  onTaskChange(task: CampaignTask, fieldName: string, value: any) {
    // Boolean fields get patched immediately, others are debounced
    if (typeof value === 'boolean') {
      this.patchTask(task, fieldName, value);
    } else {
      // Queue the task update for debounced processing
      this.taskUpdateSubject.next({ task, fieldName, value });
    }
  }

  onFileChange(fileId: string, fieldName: string, value: any) {
    if (!this.selectedDeliverable) return;

    // Queue the file update for debounced processing
    this.fileUpdateSubject.next({
      campaignId: this.campaign.id,
      deliverableId: this.selectedDeliverable.id,
      fileId,
      fieldName,
      value
    });
  }

  onUsageChange(usageId: string, fieldName: string, value: any) {
    if (!this.selectedDeliverable) return;

    // Call the patch API immediately for usage changes
    this.campaignService.patchDeliverableUsage(
      this.campaign.id,
      this.selectedDeliverable.id,
      usageId,
      fieldName,
      value
    ).subscribe({
      next: () => {
        console.log(`Usage ${fieldName} updated successfully`);
      },
      error: (error) => {
        console.error(`Failed to update usage ${fieldName}:`, error);
        // Revert the change in the UI on error
        if (this.selectedDeliverable && this.selectedDeliverable.usage) {
          const usage = this.selectedDeliverable.usage.find(u => u.id === usageId);
          if (usage) {
            // Note: This is a simplified revert - in a real app you might want to store original values
            console.log('Consider reverting the UI change');
          }
        }
      }
    });
  }

  private patchCampaignField(fieldName: string, value: any) {
    this.campaignService.patchCampaignField(this.campaign.id, fieldName, value).subscribe({
      next: () => {
        // 200 OK response indicates field was updated. Server returns no body.
        console.log(`Campaign ${fieldName} updated successfully!`);
      },
      error: (err) => {
        console.error(`Failed to update campaign ${fieldName}:`, err);
      }
    });
  }

  private patchTask(task: CampaignTask, fieldName: string, value: any) {
    // Store original value for potential revert
    const originalValue = (task as any)[fieldName];

    this.campaignService.patchCampaignTask(this.campaign.id, task.id, fieldName, value).subscribe({
      next: () => {
        console.log(`Task ${fieldName} updated successfully!`);

        // Handle special cases after successful update
        if (fieldName === 'dueDate') {
          // Re-sort tasks after due date change
          this.sortedTasks = this.campaign.tasks.sort((a, b) => {
            return a.dueDate < b.dueDate ? -1 : a.dueDate > b.dueDate ? 1 : 0;
          });
        }
      },
      error: (err) => {
        console.error(`Failed to update task ${fieldName}:`, err);

        // Revert to original value on error for boolean fields
        if (typeof originalValue === 'boolean') {
          (task as any)[fieldName] = originalValue;
        }
      }
    });
  }

  shiftDueDates() {
    if (!this.shiftDays || this.selectedTaskIds.size === 0) return;
    const tasksToShift = this.sortedTasks.filter(t => !t.complete && this.selectedTaskIds.has(t.id));
    for (const task of tasksToShift) {
      const d = new Date(task.dueDate + 'T00:00:00');
      d.setDate(d.getDate() + this.shiftDays);
      const newDate = d.toISOString().substring(0, 10);
      task.dueDate = newDate;
      this.patchTask(task, 'dueDate', newDate);
    }
    this.selectedTaskIds.clear();
    this.selectAllTasks = false;
  }

  toggleSelectAllTasks(checked: boolean) {
    this.selectAllTasks = checked;
    if (checked) {
      this.sortedTasks.filter(t => !t.complete).forEach(t => this.selectedTaskIds.add(t.id));
    } else {
      this.selectedTaskIds.clear();
    }
  }

  private patchDeliverable(deliverable: any, fieldName: string, value: any) {
    // Store original value for potential revert
    const originalValue = (deliverable as any)[fieldName];

    this.campaignService.patchCampaignDeliverable(this.campaign.id, deliverable.id, fieldName, value).subscribe({
      next: () => {
        console.log(`Deliverable ${fieldName} updated successfully!`);
      },
      error: (err) => {
        console.error(`Failed to update deliverable ${fieldName}:`, err);
        // Revert to original value on error
        (deliverable as any)[fieldName] = originalValue;
      }
    });
  }

  private patchExclusivity(exclusivity: any, fieldName: string, value: any) {
    // Store original value for potential revert
    const originalValue = (exclusivity as any)[fieldName];

    this.campaignService.patchCampaignExclusivity(this.campaign.id, exclusivity.id, fieldName, value).subscribe({
      next: () => {
        console.log(`Exclusivity ${fieldName} updated successfully!`);
      },
      error: (err) => {
        console.error(`Failed to update exclusivity ${fieldName}:`, err);
        // Revert to original value on error
        (exclusivity as any)[fieldName] = originalValue;
      }
    });
  }

  private patchDeliverableFile(campaignId: string, deliverableId: string, fileId: string, fieldName: string, value: any) {
    this.campaignService.patchDeliverableFile(campaignId, deliverableId, fileId, fieldName, value).subscribe({
      next: () => {
        console.log(`Deliverable file ${fieldName} updated successfully!`);
      },
      error: (err) => {
        console.error(`Failed to update deliverable file ${fieldName}:`, err);
      }
    });
  }

 formatDate(dateStr: string): string {
    const [year, month, day] = dateStr.split('-');
    const monthNames = [
      'January', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    return `${parseInt(day)} ${monthNames[parseInt(month) - 1]}, ${year}`;
  }

  invoiceClick(invoice: Invoice, event: MouseEvent) {
    event.preventDefault();
    this.selectedDeliverable = null;
    this.selectedFile = null;
    this.selectedInvoice = invoice;
    this.refreshInvoicePdf();
  }

  copyCampaignId() {
    var campaignIdString = `campaignid=${this.campaign.id}`;
    setTimeout(() => { navigator.clipboard.writeText(campaignIdString); }, 500);
  }

  get createdDateStr(): string {
    if (!this.campaign.createdDate) return ''; // Handle null/undefined
    const dateObj = new Date(this.campaign.createdDate); // Ensure it's a Date object
    return isNaN(dateObj.getTime()) ? '' : dateObj.toISOString().split('T')[0];
  }

  // get dueDateStr(): string {
  //   if (!this.campaign.paymentDueDate) return ''; // Handle null/undefined
  //   const dateObj = new Date(this.campaign.paymentDueDate); // Ensure it's a Date object
  //   return isNaN(dateObj.getTime()) ? '' : dateObj.toISOString().split('T')[0];
  // }

  // set dueDateStr(value: string) {
  //   this.campaign.paymentDueDate = value ? new Date(value) : new Date();
  // }

  refreshInvoicePdf() {

    if (this.selectedInvoice === null) {
      return;
    }

    let invoice = this.selectedInvoice!;

    const doc = new jsPDF();  // Default A4 - 210mm x 297mm

        // Header
        doc.setFontSize(22);
        doc.text(`Invoice`, 15, 15);
        doc.text('Vertical Talent, LLC', 210 - 15, 15, { align: 'right' });

        // Invoice details
        doc.setFontSize(12);
        doc.text(`Invoice Number`, 15, 35);
        doc.text(`${invoice.invoiceNumber}`, 55, 35);

        doc.text('Date of Issue', 15, 41);
        doc.text(`${this.formatDate(invoice.issueDate!)}`, 55, 41);

        doc.text('Date Due', 15, 47);
        doc.text(`${this.formatDate(invoice.dueDate!)}`, 55, 47);

        // Bill From
        doc.setFont('helvetica', 'bold');
        doc.text('Bill From:', 15, 59);
        doc.setFont('helvetica', 'normal');
        doc.text('Vertical Talent, LLC', 15, 65);
        doc.text('575 5th Ave. FL 14', 15, 71);
        doc.text('New York, NY 10017', 15, 77);

        // Bill To
        doc.setFont('helvetica', 'bold');
        doc.text('Bill To:', 125, 59);
        doc.setFont('helvetica', 'normal');

        let billToArray = invoice.billTo ? invoice.billTo.split('\n') : [];

        if (billToArray && billToArray.length > 0) {
          billToArray.forEach((bt, index) => {
            doc.text(bt, 125, 65 + (index * 6) )
          });
        }

        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        const totalAmount = invoice.lineItems.reduce((sum, item) => sum + item.unitPrice, 0);
        const totalAmountString = totalAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
        doc.text(`${totalAmountString} USD due ${this.formatDate(invoice.dueDate!)}`, 15, 89);

        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Description', 15, 101);
        doc.text('Amount', 170, 101);

        doc.line(13, 103, 185, 103);

        doc.setFont('helvetica', 'normal');

        invoice.lineItems.forEach((lineItem, index) => {
          doc.text(`${lineItem.description}`, 15, 108 + (index * 6));
          const totalLinePriceString = lineItem.unitPrice.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
          doc.text(`${totalLinePriceString}`, 183, 108 + (index * 6), { align: 'right' });
        });

        doc.line(120, 108 + (invoice.lineItems.length * 6), 185, 108 + (invoice.lineItems.length * 6));
        doc.setFont('helvetica', 'bold');
        doc.text('Amount Due', 121, 112 + (invoice.lineItems.length * 6));
        doc.text(`${totalAmountString}`, 183, 112 + (invoice.lineItems.length * 6), { align: 'right' });
        doc.line(120, 114 + (invoice.lineItems.length * 6), 185, 114 + (invoice.lineItems.length * 6));

        // ACH Bank Transfer Information (First)
        doc.setFont('helvetica', 'bold');
        doc.text('ACH Bank Transfer Information - Preferred Payment Method', 15, 150 + (invoice.lineItems.length * 6));
        doc.line(14, 151 + (invoice.lineItems.length * 6), 120, 151 + (invoice.lineItems.length * 6));
        doc.setFont('helvetica', 'normal');
        doc.text('Bank Name', 15, 156 + (invoice.lineItems.length * 6));
        doc.text('JPMorgan Chase Bank, N.A.', 120, 156 + (invoice.lineItems.length * 6), { align: 'right' });
        doc.text('Account Number', 15, 162 + (invoice.lineItems.length * 6));
        doc.text('692712121', 120, 162 + (invoice.lineItems.length * 6), { align: 'right' });
        doc.text('Routing Number', 15, 168 + (invoice.lineItems.length * 6));
        doc.text('322271627', 120, 168 + (invoice.lineItems.length * 6), { align: 'right' });

        // Wire Transfer Information (Second, 30 units below ACH)
        doc.setFont('helvetica', 'bold');
        doc.text('Wire Transfer Information', 15, 180 + (invoice.lineItems.length * 6));
        doc.line(14, 182 + (invoice.lineItems.length * 6), 120, 182 + (invoice.lineItems.length * 6));
        doc.setFont('helvetica', 'normal');
        doc.text('Bank Name:', 15, 186 + (invoice.lineItems.length * 6));
        doc.text('J.P. Morgan Chase, N.A.', 120, 186 + (invoice.lineItems.length * 6), { align: 'right' });
        doc.text('Bank Address:', 15, 192 + (invoice.lineItems.length * 6));
        doc.text('270 Park Avenue, New York, NY 10017', 120, 192 + (invoice.lineItems.length * 6), { align: 'right' });
        doc.text('Routing Number (Domestic Wire):', 15, 198 + (invoice.lineItems.length * 6));
        doc.text('021000021', 120, 198 + (invoice.lineItems.length * 6), { align: 'right' });
        doc.text('SWIFT Code (International Wire):', 15, 204 + (invoice.lineItems.length * 6));
        doc.text('CHASUS33', 120, 204 + (invoice.lineItems.length * 6), { align: 'right' });
        doc.text('ABA Number:', 15, 210 + (invoice.lineItems.length * 6));
        doc.text('021000021', 120, 210 + (invoice.lineItems.length * 6), { align: 'right' });
        doc.text('Recipient Account Business Name', 15, 216 + (invoice.lineItems.length * 6));
        doc.text('VERTICAL TALENT LLC', 120, 216 + (invoice.lineItems.length * 6), { align: 'right' });
        doc.text('Account Number', 15, 222 + (invoice.lineItems.length * 6));
        doc.text('692712121', 120, 222 + (invoice.lineItems.length * 6), { align: 'right' });

        // Invoice Notes - positioned after Wire Transfer Information
        let invoiceNotesArray = invoice.invoiceNotes ? invoice.invoiceNotes.split("\n") : [];

        if (invoiceNotesArray && invoiceNotesArray.length > 0) {
          invoiceNotesArray.forEach((note, index) => {
            doc.text(note, 15, 234 + (invoice.lineItems.length * 6) + (index * 6) )
          });
        }

        doc.text(`${invoice.invoiceNumber} ${totalAmountString} USD. Due ${this.formatDate(invoice.dueDate!)}`, 15, 282);
        doc.text(`Page 1 of 1`, 210-15, 282, { align: 'right' });

        setTimeout(() => {
          // Set the filename for when user saves the PDF
          const filename = `Invoice-${invoice.invoiceNumber}.pdf`;

          // Store the PDF doc for potential download
          (this as any).currentInvoicePdf = { doc, filename };

          // Use data URI to display in iframe
          const pdfDataUri = doc.output('datauristring');

          const iframe = document.createElement('iframe');
          iframe.src = pdfDataUri;
          iframe.width = '100%';
          iframe.height = '100%';
          iframe.style.border = 'none';
          iframe.title = filename;

          const container = this.pdfPreview.nativeElement;
          container.innerHTML = '';
          container.appendChild(iframe);
        }, 0); // or 50ms if needed
  }

  downloadCurrentInvoice() {
    if ((this as any).currentInvoicePdf) {
      const { doc, filename } = (this as any).currentInvoicePdf;
      doc.save(filename);
    }
  }

  loadCampaignVideos() {
    if (!this.campaign?.id) return;
    this.isLoadingVideos = true;
    this.campaignVideoService.getVideos(this.campaign.id).subscribe({
      next: (videos) => {
        this.campaignVideos = videos;
        this.isLoadingVideos = false;
      },
      error: (err) => {
        console.error('Failed to load campaign videos', err);
        this.isLoadingVideos = false;
      }
    });
  }

  addCampaignVideo() {
    if (!this.newVideoUrl?.trim() || this.isAddingVideo) return;
    this.isAddingVideo = true;
    this.addVideoError = '';
    this.campaignVideoService.addVideo(this.campaign.id, this.newVideoUrl.trim()).subscribe({
      next: (video) => {
        this.campaignVideos = [...this.campaignVideos, video];
        this.newVideoUrl = '';
        this.isAddingVideo = false;
      },
      error: (err) => {
        this.addVideoError = err?.error || 'Could not add that video. Check the URL.';
        this.isAddingVideo = false;
      }
    });
  }

  refreshCampaignVideo(video: CampaignVideo) {
    if (this.refreshingVideoId) return;
    this.refreshingVideoId = video.id;
    this.campaignVideoService.refreshVideo(this.campaign.id, video.id).subscribe({
      next: (updated) => {
        const idx = this.campaignVideos.findIndex(v => v.id === updated.id);
        if (idx >= 0) this.campaignVideos[idx] = updated;
        this.refreshingVideoId = null;
      },
      error: (err) => {
        console.error('Failed to refresh video', err);
        this.refreshingVideoId = null;
      }
    });
  }

  deleteCampaignVideo(video: CampaignVideo) {
    if (!confirm('Remove this video?')) return;
    this.campaignVideoService.deleteVideo(this.campaign.id, video.id).subscribe({
      next: () => {
        this.campaignVideos = this.campaignVideos.filter(v => v.id !== video.id);
      },
      error: (err) => {
        console.error('Failed to delete video', err);
      }
    });
  }

}
